import {
  ButtonDisplayMode,
  ButtonHierarchy,
  ButtonSize,
  MainButton,
} from "@beydesign/storybook"
import { ChatMessage } from "@livekit/components-core"
import { useChat, useRoomContext } from "@livekit/components-react"
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material"
import { PaperPlaneTilt, X } from "@phosphor-icons/react"
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { Colors, getPrimaryColor } from "../../constants/colors"
import { ChatBox } from "./ChatBox"

type UseChatReturnType = ReturnType<typeof useChat>

interface CustomChatBoxProps extends UseChatReturnType {
  showChat: boolean
  closeChat: () => void
  openChat: () => void
}

interface ChatBoxProps {
  showChat: boolean
  closeChat: () => void
  openChat: () => void
  chatMessages: ChatMessage[]
  chatContainerRef: React.RefObject<HTMLDivElement>
  messagesEndRef: React.RefObject<HTMLDivElement>
  text: string
  setText: (text: string) => void
  handleSend: () => void
}

interface ChatInputProps {
  text: string
  setText: (text: string) => void
  handleSend: () => void
}

interface ChatMessagesProps {
  chatMessages: ChatMessage[]
  chatContainerRef: React.RefObject<HTMLDivElement>
  messagesEndRef: React.RefObject<HTMLDivElement>
}

const ChatInput = React.memo(
  ({ text, setText, handleSend }: ChatInputProps) => (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
      flexDirection={"row"}
      height={{ xs: 40, sm: 50 }}
      sx={{ backgroundColor: "#F5F5F7" }}
      borderRadius={"1.5rem"}
      padding={{ xs: "0.75rem", sm: "1rem" }}
      gap={1}
      data-testid="chat-input-container"
    >
      <input
        type="text"
        style={{
          width: "80%",
          height: "100%",
          backgroundColor: "#F5F5F7",
          border: "none",
          color: "black",
          outline: "none",
          fontSize: "inherit",
        }}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSend()
          }
        }}
        placeholder="Send a message"
        key={"chat-input"}
        name={"chat-input"}
        autoComplete="off"
        autoFocus
        data-testid="chat-input-field"
      />
      <Box
        sx={{
          cursor: "pointer",
          aspectRatio: 1,
          backgroundColor: getPrimaryColor(),
        }}
        width={{ xs: 24, sm: 30 }}
        height={{ xs: 24, sm: 30 }}
        borderRadius={"1.5rem"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        onClick={handleSend}
        data-testid="chat-send-button"
      >
        <PaperPlaneTilt color={Colors.White} size={20} />
      </Box>
    </Box>
  ),
)

const ChatMessages = React.memo(
  ({ chatMessages, chatContainerRef, messagesEndRef }: ChatMessagesProps) => (
    <Box
      ref={chatContainerRef}
      data-testid="chat-message-container"
      display={"flex"}
      flexDirection={"column"}
      flex={1}
      overflow={"auto"}
      gap={1.5}
      marginY={1}
      sx={{
        "&::-webkit-scrollbar": {
          width: "0.5rem",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#F5F5F7",
          borderRadius: "0.25rem",
        },
        maxHeight: "calc(100% - 120px)",
      }}
    >
      {chatMessages.map((message) => (
        <ChatBox key={message.id} item={message} />
      ))}
      <div ref={messagesEndRef} />
    </Box>
  ),
)

const ChatBoxMobile = React.memo(
  ({
    showChat,
    closeChat,
    chatMessages,
    chatContainerRef,
    messagesEndRef,
    text,
    setText,
    handleSend,
  }: ChatBoxProps) => {
    if (!showChat) return null
    return (
      <Box
        sx={{
          backgroundColor: "var(--primitives-desktop-primary-white-40)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "50vh",
        }}
        position={"absolute"}
        right={"2rem"}
        left={"2rem"}
        bottom={"2rem"}
        zIndex={1000}
        borderRadius={"var(--spacing-tokens-size-in-px-radius-radius-xl)"}
        padding={"1rem"}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          flexDirection={"row"}
          width={"100%"}
        >
          <Typography
            variant="body1"
            color="black"
            fontWeight={600}
            fontSize={"0.9rem"}
          >
            Conversation
          </Typography>
          <MainButton
            hierarchy={ButtonHierarchy.Tetriary}
            displayIconMode={ButtonDisplayMode.Only}
            displayIcon="X"
            onClick={closeChat}
            size={ButtonSize.md}
          />
        </Box>
        {showChat && (
          <Fragment>
            <ChatMessages
              chatMessages={chatMessages}
              chatContainerRef={chatContainerRef}
              messagesEndRef={messagesEndRef}
            />
            <ChatInput text={text} setText={setText} handleSend={handleSend} />
          </Fragment>
        )}
      </Box>
    )
  },
)

const ChatBoxPC = React.memo(
  ({
    showChat,
    closeChat,
    chatMessages,
    chatContainerRef,
    messagesEndRef,
    text,
    setText,
    handleSend,
  }: ChatBoxProps) => {
    if (!showChat) return null
    return (
      <Box
        sx={{ backgroundColor: "var(--primitives-desktop-primary-white-40)" }}
        display={"flex"}
        flexDirection={"column"}
        borderRadius={"12px"}
        padding={"1rem"}
        width={"29%"}
        position={"absolute"}
        right={"var(--spacing-tokens-size-in-px-spacing-spacing-4xl)"}
        bottom={"var(--spacing-tokens-size-in-px-spacing-spacing-4xl)"}
        height={"calc(100% - 120px)"}
        ml={2}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          height={"100%"}
          position={"relative"}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            flexDirection={"row"}
            width={"100%"}
          >
            <Typography
              variant="body1"
              color="black"
              fontWeight={600}
              fontSize={{ xs: "0.875rem", sm: "1rem" }}
            >
              Conversation
            </Typography>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              width={40}
              height={40}
              onClick={closeChat}
              sx={{ cursor: "pointer" }}
            >
              <X color="black" size={24} />
            </Box>
          </Box>
          <ChatMessages
            chatMessages={chatMessages}
            chatContainerRef={chatContainerRef}
            messagesEndRef={messagesEndRef}
          />
          <ChatInput text={text} setText={setText} handleSend={handleSend} />
        </Box>
      </Box>
    )
  },
)

const CustomChatBoxComponent = ({
  showChat,
  closeChat,
  openChat,
  chatMessages,
  isSending,
  send,
  update,
}: CustomChatBoxProps) => {
  const [text, setText] = useState("")
  const [isNearBottom, setIsNearBottom] = useState(true)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const room = useRoomContext()

  const checkIfNearBottom = useCallback(() => {
    const container = chatContainerRef.current
    if (!container) return true

    const threshold = 150
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold
    )
  }, [])

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      if (isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior })
      }
    },
    [isNearBottom],
  )

  const handleScroll = useCallback(() => {
    setIsNearBottom(checkIfNearBottom())
  }, [checkIfNearBottom])

  useEffect(() => {
    const container = chatContainerRef.current
    if (container) {
      container.addEventListener("scroll", handleScroll)
      return () => container.removeEventListener("scroll", handleScroll)
    }
  }, [handleScroll])

  useEffect(() => {
    scrollToBottom("smooth")
  }, [chatMessages.length, scrollToBottom])

  useEffect(() => {
    if (showChat) {
      scrollToBottom()
    }
  }, [showChat, scrollToBottom])

  const handleSend = useCallback(async () => {
    if (text.trim() === "") return

    try {
      if (room?.localParticipant) {
        await room.localParticipant.sendText(text, {
          topic: "lk.chat",
        })
      }
    } catch (e) {
      console.error("Failed to send message:", e)
    }

    await send(text)

    setText("")
    setIsNearBottom(true)
    setTimeout(() => scrollToBottom("smooth"), 100)
  }, [text, send, scrollToBottom, room?.localParticipant])

  const Component = isMobile ? ChatBoxMobile : ChatBoxPC

  return (
    <Component
      showChat={showChat}
      closeChat={closeChat}
      openChat={openChat}
      chatMessages={chatMessages}
      chatContainerRef={chatContainerRef}
      messagesEndRef={messagesEndRef}
      text={text}
      setText={setText}
      handleSend={handleSend}
    />
  )
}

export const CustomChatBox = React.memo(
  CustomChatBoxComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.showChat === nextProps.showChat &&
      prevProps.isSending === nextProps.isSending &&
      prevProps.chatMessages === nextProps.chatMessages
    )
  },
)
